/**
 * Construção e normalização de valores do contexto de avaliação.
 *
 * Paridade com o backend (`ValorContexto`): a normalização de texto é `trim()` + `toLowerCase()`.
 * `trim()` (JS) e `strip()` (Java) removem conjuntos de espaços Unicode ligeiramente diferentes, e
 * `toLowerCase` diverge em pouquíssimos caracteres (ex.: `İ`). A avaliação local é só UX — quem
 * decide é o backend, que revalida no envio.
 */
import { ValorContexto } from './tipos'

export const valorNumero = (v: number): ValorContexto => ({ tipo: 'numero', v })

export const valorTexto = (v: string | null | undefined): ValorContexto => ({
  tipo: 'texto',
  v: v == null ? '' : v,
})

/** Textos em branco são descartados, como no backend. */
export const valorTextos = (
  v: Array<string | null | undefined> | null | undefined
): ValorContexto => ({
  tipo: 'textos',
  v: (v ?? []).filter((item): item is string => typeof item === 'string' && item.trim() !== ''),
})

/** `true` quando o valor, na prática, não existe (texto em branco ou lista vazia). */
export const ausente = (valor: ValorContexto | null | undefined): boolean => {
  if (valor == null) return true
  switch (valor.tipo) {
    case 'numero':
      return false
    case 'texto':
      return valor.v.trim() === ''
    default:
      return valor.v.length === 0
  }
}

/** Normalização usada em toda comparação textual. */
export const normalizar = (texto: string | null | undefined): string =>
  texto == null ? '' : texto.trim().toLowerCase()

/**
 * Lê um número a partir de texto, aceitando vírgula ou ponto como separador decimal.
 *
 * Com vírgula presente, o ponto é separador de milhar (`"1.234,5"`); sem vírgula, o ponto é o
 * separador decimal (`"37.8"`). Texto que não é número devolve `null` (= ausente).
 */
export const numeroDeTexto = (texto: string | null | undefined): number | null => {
  if (texto == null || texto.trim() === '') return null
  const bruto = texto.trim()
  const normalizado = bruto.indexOf(',') >= 0 ? bruto.replace(/\./g, '').replace(',', '.') : bruto
  if (!/^[+-]?(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?$/.test(normalizado)) return null
  const numero = Number(normalizado)
  return Number.isFinite(numero) ? numero : null
}

/** Converte um valor solto (resposta, variável resolvida) no `ValorContexto` correspondente. */
export const valorDeDesconhecido = (valor: unknown): ValorContexto | null => {
  if (valor == null) return null
  if (typeof valor === 'number') return Number.isFinite(valor) ? valorNumero(valor) : null
  if (Array.isArray(valor)) return valorTextos(valor.map((item) => String(item)))
  if (typeof valor === 'boolean') return valorTexto(String(valor))
  return valorTexto(String(valor))
}
